import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { listExperiences } from "@/lib/experiences.functions";
import { ExperiencesSection } from "@/components/admin/ExperiencesSection";
import { UsersSection } from "@/components/admin/UsersSection";
import { ThemeSection } from "@/components/admin/ThemeSection";
import { supabase } from "@/integrations/supabase/client";
import { ChevronRight, LogOut, KeyRound } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/")({
  head: () => ({ meta: [{ title: "Admin" }, { name: "robots", content: "noindex" }] }),
  component: AdminPage,
});

function AdminPage() {
  const fetchExps = useServerFn(listExperiences);
  const navigate = useNavigate();

  const { data: experiences } = useQuery({
    queryKey: ["experiences"],
    queryFn: () => fetchExps(),
  });

  const exps = experiences ?? [];

  return (
    <div
      className="fixed inset-0 overflow-y-auto bg-background text-foreground"
      style={{ touchAction: "auto", overscrollBehavior: "auto" }}
    >
      <Toaster richColors closeButton position="top-right" />
      <div className="max-w-3xl mx-auto px-6 py-10 space-y-10">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-serif">Site CMS</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage experience paths, category pages, and admin access.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate({ to: "/" })}>
              View site
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate({ to: "/account" })}>
              <KeyRound className="w-4 h-4 mr-2" /> Password
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => {
                await supabase.auth.signOut();
                navigate({ to: "/login" });
              }}
            >
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>

        <UsersSection />

        <ThemeSection />

        <ExperiencesSection experiences={exps} />

        <section className="space-y-3">
          <div>
            <h2 className="text-lg font-serif">Suites</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Edit suite descriptions, feature tags, and manage gallery photos.
            </p>
          </div>
          <Link
            to="/admin/suites"
            className="inline-flex items-center justify-between w-full px-4 py-3 rounded-lg border border-border bg-card hover:bg-accent transition-colors"
          >
            <span className="font-serif">Manage Suites CMS</span>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </Link>
        </section>

        <section className="space-y-3">
          <div>
            <h2 className="text-lg font-serif">Culinary</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Edit the culinary hero, dining inclusions, menu images, and gallery.
            </p>
          </div>
          <Link
            to="/admin/culinary"
            className="inline-flex items-center justify-between w-full px-4 py-3 rounded-lg border border-border bg-card hover:bg-accent transition-colors"
          >
            <span className="font-serif">Manage Culinary CMS</span>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </Link>
        </section>

        <section className="space-y-3">
          <div>
            <h2 className="text-lg font-serif">Intro (Home)</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Edit the homepage rotating words, featured-in label, statement section, and reviews headings.
            </p>
          </div>
          <Link
            to="/admin/intro"
            className="inline-flex items-center justify-between w-full px-4 py-3 rounded-lg border border-border bg-card hover:bg-accent transition-colors"
          >
            <span className="font-serif">Manage Intro CMS</span>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </Link>
        </section>

        <section className="space-y-3">
          <div>
            <h2 className="text-lg font-serif">Things To Do</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Edit both the Onsite (amenities) and Nearby (attractions) sections.
            </p>
          </div>
          <Link
            to="/admin/things-to-do"
            className="inline-flex items-center justify-between w-full px-4 py-3 rounded-lg border border-border bg-card hover:bg-accent transition-colors"
          >
            <span className="font-serif">Manage Things To Do CMS</span>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </Link>
        </section>

        <section className="space-y-3">
          <div>
            <h2 className="text-lg font-serif">Stay Enhancements</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Manage the Pet Fee, Romance, Unwind & Reset, and Celebration cards on the Suites page.
            </p>
          </div>
          <Link
            to="/admin/enhancements"
            className="inline-flex items-center justify-between w-full px-4 py-3 rounded-lg border border-border bg-card hover:bg-accent transition-colors"
          >
            <span className="font-serif">Manage Stay Enhancements CMS</span>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </Link>
        </section>

        <section className="space-y-3">
          <div>
            <h2 className="text-lg font-serif">Blog</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Create, edit, and publish blog posts. Manage the /blog index page hero and SEO.
            </p>
          </div>
          <Link
            to="/admin/blog"
            className="inline-flex items-center justify-between w-full px-4 py-3 rounded-lg border border-border bg-card hover:bg-accent transition-colors"
          >
            <span className="font-serif">Manage Blog CMS</span>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </Link>
        </section>

        <section className="space-y-3">
          <div>
            <h2 className="text-lg font-serif">Groups & Events</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Edit the Groups & Events hero, event type cards, FAQ, and gallery.
            </p>
          </div>
          <Link
            to="/admin/groups-events"
            className="inline-flex items-center justify-between w-full px-4 py-3 rounded-lg border border-border bg-card hover:bg-accent transition-colors"
          >
            <span className="font-serif">Manage Groups & Events CMS</span>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </Link>
        </section>

      </div>
    </div>
  );
}
